import { api } from "./api";

/**
 * Roles and per-user permissions.
 *
 * Contract taken from "RemnutriApi'sCreatedByChethan" (folders 05, 06 and
 * 07 - Role management), supplied 2 Nov 2026.
 *
 * DEPLOYED 2 Nov 2026. These were absent earlier the same day; re-verified
 * live against https://remnutri.nextechltd.in with an admin token:
 *
 *     POST /roles/getAll              -> 4 roles, with role_code and access_to
 *     POST /roles/add|updateById|getById -> validation errors (routes exist)
 *     POST /practitioner/getByRole    -> 5 practitioners
 *     GET  /user-permissions/{id}     -> { user_id, main_modules: [] }
 *     GET  /permission-modules/hierarchy -> []
 *
 * TWO THINGS THAT CAUGHT US OUT, both recorded so they are not re-introduced:
 *
 * 1. The save is POST, not the PATCH the collection mostly shows. See
 *    UpdateUserPermissionsApi below.
 *
 * 2. The module tree is EMPTY. Both the hierarchy and every user's
 *    main_modules come back as [], so there is nothing to grant yet — the
 *    permission modules have never been seeded with
 *    POST /permission-modules/main_module|module|sub_module (folders 01-03 of
 *    the collection). The Authorization screen says so rather than rendering
 *    a blank grid.
 *
 * The screens that call these therefore render a "hasn't shipped yet" state
 * until the backend is deployed — see isRouteMissing in utils/apiError.
 */

/* ------------------------------------------------------------------ roles */

export interface RoleRecord {
  role_id: string;
  role_type: string;
  role_code: string | null;
  /** Legacy per-feature map on the seeded roles; `{}` on newly created ones. */
  access_to?: Record<string, boolean>;
  created_date?: string;
  update_date?: string;
}

/** All roles. POST with an empty body — the collection's own example sends {}. */
export const ListRolesApi = () => api.post(`/roles/getAll`, {});

export const GetRoleByIdApi = (role_id: string) =>
  api.post(`/roles/getById`, { role_id });

/**
 * `role_code` must be unique across roles — the collection says so explicitly,
 * which is why a duplicate is worth reporting as a name clash rather than as a
 * generic failure.
 */
export const AddRoleApi = (params: {
  role_type: string;
  role_code: string;
  access_to?: Record<string, boolean>;
}) =>
  api.post(`/roles/add`, {
    role_type: params.role_type,
    role_code: params.role_code,
    access_to: params.access_to ?? {},
  });

/**
 * Only the fields sent are changed. The collection's example sends role_type
 * alone and the response keeps the existing role_code, so this mirrors that
 * and omits anything the caller left undefined.
 */
export const UpdateRoleApi = (params: {
  role_id: string;
  role_type?: string;
  role_code?: string;
  access_to?: Record<string, boolean>;
}) => {
  const body: Record<string, unknown> = { role_id: params.role_id };
  if (params.role_type !== undefined) body.role_type = params.role_type;
  if (params.role_code !== undefined) body.role_code = params.role_code;
  if (params.access_to !== undefined) body.access_to = params.access_to;
  return api.post(`/roles/updateById`, body);
};

/*
 * NO DELETE. The collection's folder description says deletion "is in the
 * cleanup folder and returns 409 if any user still has that role", but the
 * cleanup folder holds only practitioner and permission-module deletes — there
 * is no role delete request anywhere in the file. Rather than guess a path,
 * the UI leaves role deletion out. Ask the backend for the route.
 */

/** Practitioners holding one role. */
export const GetPractitionersByRoleApi = (user_role_id: string) =>
  api.post(`/practitioner/getByRole`, { user_role_id });

/* ------------------------------------------------------ user permissions */

/** The nine permission flags each row carries. `all` is a row-level shortcut. */
export const PERMISSION_FLAGS = [
  "create",
  "read",
  "update",
  "delete",
  "print",
  "export",
  "send",
  "approver",
] as const;

export type PermissionFlag = (typeof PERMISSION_FLAGS)[number];

export type ModuleType = "main_module" | "module" | "sub_module";

export interface PermissionNode {
  module_type: ModuleType;
  id: string;
  module_name: string;
  description?: string;
  permission_id?: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  print: boolean;
  export: boolean;
  send: boolean;
  approver: boolean;
  all: boolean;
  modules?: PermissionNode[];
  sub_modules?: PermissionNode[];
}

export interface UserPermissionsResponse {
  user_id: string;
  main_modules: PermissionNode[];
}

/** The whole module tree for one user, with every flag's current value. */
export const GetUserPermissionsApi = (user_id: string) =>
  api.get(`/user-permissions/${user_id}`);

/**
 * Save changed rows.
 *
 * POST, not PATCH — verified against the live server on 2 Nov 2026:
 *
 *     POST  /user-permissions/{id}  -> "permissions" must contain at least 1 items
 *     PATCH /user-permissions/{id}  -> "Page not found"
 *     PUT   /user-permissions/{id}  -> "Page not found"
 *
 * A validation error means the route exists and accepted the method; "Page not
 * found" is what this API returns for an unrouted method+path pair. The
 * collection is contradictory here — four of its five update requests declare
 * PATCH and one declares POST — and the deployed server implements POST. This
 * was built to PATCH first and would have failed on every save.
 *
 * Semantics taken from the collection's descriptions:
 *   - Omitted flags keep their existing values, so only changed rows are sent.
 *   - `all` affects that row only, never its children.
 *   - An explicit flag overrides `all`, and the row comes back with all=false.
 *   - The batch is atomic: one invalid target rolls the whole call back.
 */
export const UpdateUserPermissionsApi = (
  user_id: string,
  permissions: Array<{ module_type: ModuleType; id: string } & Partial<Record<PermissionFlag | "all", boolean>>>
) => api.post(`/user-permissions/${user_id}`, { permissions });

/* ---------------------------------------------------- permission modules */

/** The module tree itself, independent of any user. */
export const GetModuleHierarchyApi = () => api.get(`/permission-modules/hierarchy`);
